/**
 * Post once, everywhere: what each platform accepts, so the composer can warn before publishing (instead of the
 * platform rejecting the post afterwards). VERIFY limits against each platform's current documentation.
 */
export interface PostLimits {
  maxChars: number;
  /** The platform cannot publish text alone. */
  mediaRequired: boolean;
  maxMedia: number;
  /** Media kinds accepted. */
  accepts: ('image' | 'video')[];
  /** Links in the text are clickable. */
  links: boolean;
}

export const POST_LIMITS: Record<string, PostLimits> = {
  meta_facebook: {
    maxChars: 63_206,
    mediaRequired: false,
    maxMedia: 10,
    accepts: ['image', 'video'],
    links: true,
  },
  meta_instagram: {
    maxChars: 2_200,
    mediaRequired: true,
    maxMedia: 10,
    accepts: ['image', 'video'],
    links: false,
  },
  tiktok_business: {
    maxChars: 2_200,
    mediaRequired: true,
    maxMedia: 35,
    accepts: ['image', 'video'],
    links: false,
  },
  x_account: { maxChars: 280, mediaRequired: false, maxMedia: 4, accepts: ['image', 'video'], links: true },
  linkedin_page: {
    maxChars: 3_000,
    mediaRequired: false,
    maxMedia: 9,
    accepts: ['image', 'video'],
    links: true,
  },
  google_business: { maxChars: 1_500, mediaRequired: false, maxMedia: 1, accepts: ['image'], links: true },
};

export const PUBLISH_PROVIDERS = Object.keys(POST_LIMITS);

export type PostIssue =
  | { code: 'too_long'; max: number; length: number }
  | { code: 'media_required' }
  | { code: 'too_many_media'; max: number }
  | { code: 'media_kind'; kind: string }
  | { code: 'empty' }
  | { code: 'links_not_clickable' };

/** Characters as the platforms count them (X counts a link as 23; others count code points). */
export function postLength(provider: string, text: string): number {
  const chars = [...text].length;
  if (provider !== 'x_account') return chars;
  return [...text.replace(/https?:\/\/\S+/g, 'x'.repeat(23))].length;
}

/** Problems that would stop (errors) or weaken (warnings) a post on this platform. */
export function checkPost(
  provider: string,
  text: string,
  media: { mime: string }[],
): { errors: PostIssue[]; warnings: PostIssue[] } {
  const l = POST_LIMITS[provider];
  const errors: PostIssue[] = [];
  const warnings: PostIssue[] = [];
  if (!l) return { errors, warnings };
  const length = postLength(provider, text);
  if (!text.trim() && !media.length) errors.push({ code: 'empty' });
  if (length > l.maxChars) errors.push({ code: 'too_long', max: l.maxChars, length });
  if (l.mediaRequired && !media.length) errors.push({ code: 'media_required' });
  if (media.length > l.maxMedia) errors.push({ code: 'too_many_media', max: l.maxMedia });
  for (const m of media) {
    const kind = m.mime.startsWith('video/') ? 'video' : 'image';
    if (!l.accepts.includes(kind)) errors.push({ code: 'media_kind', kind });
  }
  if (!l.links && /https?:\/\//.test(text)) warnings.push({ code: 'links_not_clickable' });
  return { errors, warnings };
}
